import { Datum, ResponsiveLine, Serie } from "@nivo/line";
import { Box, Typography } from "@mui/material";
import { senecPrimaryColors, senecSecondaryColors } from "src/styles/ekp-theme/senecEkpTheme";
import { ChartColor } from "src/components/charts/ChartColor";
import { useEffect, useState } from "react";

const chartColorMap = new Map([
    [ChartColor.YELLOW, { lineColor: "#FFC83A", areaColor: "#FEECA7" }],
    [ChartColor.ORANGE, { lineColor: "#FF8F0F", areaColor: "#F2DEC3" }],
    [ChartColor.LIGHTGRAY, { lineColor: "#AAAAAA", areaColor: "#DCDCDC" }],
    [ChartColor.DARKGRAY, { lineColor: "#5B5B5B", areaColor: "#DCDCDC" }],
]);

export function getColorValues(chartColor: ChartColor): { lineColor: string; areaColor: string } {
    const colorObj = chartColorMap.get(chartColor);
    return colorObj ?? { lineColor: "#FFC83A", areaColor: "#FEECA7" };
}

export interface GradientInfo {
    color: string;
    pixelStart: string;
    pixelEnd: string;
}

export interface LineChartProps {
    color: ChartColor;
    dataSeries: Serie[];
    maxY: number;
    errorMessage?: string;
    curve?: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter";
}

export const LineChart = ({ color, dataSeries, maxY, errorMessage, curve = "step" }: LineChartProps) => {
    const [lineWidthBox, setLineWidthBox] = useState(100);

    useEffect(() => {
        const lineWidthRef = document.getElementById("lineWidthBox");

        if (lineWidthRef) {
            const widthInPixels = lineWidthRef.offsetWidth;
            setLineWidthBox(widthInPixels);
        }
    }, []);

    return (
        <Box
            id={"lineWidthBox"}
            height={32}
            width={"100%"}
            className={"line-chart"}
            data-testid="line-chart"
            sx={{
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    width: "100%",
                    height: "2px",
                    background: senecPrimaryColors.lightWarmGrey,
                    borderRadius: "80px",
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    height: "2px",
                    background:
                        dataSeries.length === 0
                            ? senecPrimaryColors.lightWarmGrey
                            : `linear-gradient(to right, ${convertToGradientString(
                                  generateLinearGradientInfo(dataSeries[0]?.data ?? [], lineWidthBox, color),
                              )})`,
                    zIndex: 10,
                    borderRadius: getBorderRadiusFor(dataSeries[0]?.data ?? []),
                },
            }}
        >
            <Box style={{ height: "100%" }} sx={{ position: "relative", zIndex: 20 }}>
                {dataSeries.length === 0 && errorMessage && (
                    <Box sx={{ display: "grid", height: "100%", alignContent: "center" }}>
                        <Typography
                            variant={"h5"}
                            sx={{
                                color: senecSecondaryColors.neutralGray20,
                                textTransform: "none",
                                fontWeight: 700,
                                overflow: "hidden",
                                textAlign: "center",
                            }}
                            data-testid={"LineChart-Error"}
                        >
                            {errorMessage}
                        </Typography>
                    </Box>
                )}
                {dataSeries.length > 0 && (
                    <ResponsiveLine
                        data={dataSeries}
                        yScale={{
                            type: "linear",
                            min: 0,
                            max: maxY < 25 ? 32 : maxY + 0.3 * maxY,
                            stacked: true,
                            reverse: false,
                        }}
                        margin={{ bottom: 2 }}
                        axisBottom={{
                            tickSize: 0,
                            tickRotation: 0,
                            legendOffset: 0,
                        }}
                        enablePoints={true}
                        pointSize={4}
                        enableGridY={false}
                        enableGridX={false}
                        isInteractive={false}
                        lineWidth={4}
                        enableArea={true}
                        areaOpacity={1}
                        colors={[getColorValues(color).lineColor]}
                        curve={curve}
                        defs={[
                            {
                                id: `customAreaColor-${color}`,
                                type: "linearGradient",
                                colors: [{ offset: 0, color: getColorValues(color).areaColor }],
                            },
                        ]}
                        fill={[{ match: "*", id: `customAreaColor-${color}` }]}
                    />
                )}
            </Box>
        </Box>
    );
};

export function convertToGradientString(gradientInfo: GradientInfo[]): string {
    let gradientString = "";
    for (const info of gradientInfo) {
        gradientString += `${info.color} ${info.pixelStart} ${info.pixelEnd} `;
        if (gradientInfo.length != gradientInfo.indexOf(info) + 1) {
            gradientString += ", ";
        }
    }
    return gradientString;
}

export function generateLinearGradientInfo(data: readonly Datum[], lineWidth: number, color: ChartColor): GradientInfo[] {
    const gradientInfo: GradientInfo[] = [];
    let pixelCount = 0;
    let isNullStreak = false;
    let alreadyIncreasedPixelCount = false;

    for (let i = 0; i < data.length; i++) {
        if (data[i].y === null) {
            if (!isNullStreak) {
                // end the non-null streak
                if (gradientInfo.length > 0) {
                    gradientInfo[gradientInfo.length - 1].pixelEnd = `${pixelCount}px`;
                }
                // start a new null streak
                gradientInfo.push({ color: "#f2f2f2", pixelStart: `${pixelCount}px`, pixelEnd: "" });
                isNullStreak = true;
            }
        } else {
            if (isNullStreak || i === 0) {
                // For a null value the previous and the next datapoint the bottom line should not be colored, for this reason the pixelCount is increased before
                if (isNullStreak) {
                    pixelCount += lineWidth / (data.length - 1);
                    alreadyIncreasedPixelCount = true;
                }
                // end the null streak or start a new streak at the beginning
                if (gradientInfo.length > 0) {
                    gradientInfo[gradientInfo.length - 1].pixelEnd = `${pixelCount}px`;
                }
                gradientInfo.push({ color: getColorValues(color).areaColor, pixelStart: `${pixelCount}px`, pixelEnd: "" });
                isNullStreak = false;
            }
        }
        if (i != 0 && !alreadyIncreasedPixelCount) {
            pixelCount += lineWidth / (data.length - 1);
        } else {
            alreadyIncreasedPixelCount = false;
        }
    }

    // end the last streak
    if (gradientInfo.length > 0) {
        gradientInfo[gradientInfo.length - 1].pixelEnd = `${pixelCount}px`;
    }

    return gradientInfo;
}

export function getBorderRadiusFor(data: readonly Datum[]): string {
    // Round line when first point is null or when first point is set and second point is null
    const firstPointRound = data[0]?.y === null || (data[0]?.y != null && data[1]?.y === null);
    const secondPointRound =
        data[data.length - 1]?.y === null || (data[data.length - 1]?.y != null && data[data.length - 2]?.y === null);
    const firstPoint = firstPointRound ? "80px " : "0px ";
    return secondPointRound ? firstPoint + "80px 80px 80px" : firstPoint + "0px 80px 80px";
}
